import { useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/features/auth/AuthContext";
import { useToast } from "@/ui/primitives/use-toast";
import { Checkbox } from "@/ui/primitives/checkbox";
import { LGoButton, LGoFormField } from "@/ui/branded";
import { registerSchema, type RegisterInput } from "@/domain/user/schema";
import { PARTNER_TERMS_URL, PARTNER_TERMS_VERSION } from "@/domain/user/legal";

interface RegisterFormProps {
  onSuccess: () => void;
}

export function RegisterForm({ onSuccess }: RegisterFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();
  const { toast } = useToast();

  const form = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      accessCode: "",
      acceptedTerms: false,
    },
  });

  const onSubmit = async (data: RegisterInput) => {
    setIsLoading(true);
    const { error } = await signUp(
      data.email,
      data.password,
      data.accessCode,
      PARTNER_TERMS_VERSION,
    );
    setIsLoading(false);

    if (error) {
      const description = error.message.includes("already registered")
        ? "Uživatel s tímto emailem již existuje"
        : error.message;
      toast({ variant: "destructive", title: "Chyba registrace", description });
      return;
    }

    toast({
      title: "Registrace úspěšná",
      description:
        "Poslali jsme vám potvrzovací odkaz na e-mail. Otevřete ho a pak se můžete přihlásit.",
    });
    onSuccess();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <LGoFormField name="email" label="Email" type="email" placeholder="vas@email.cz" />
        <LGoFormField name="password" label="Heslo" type="password" placeholder="••••••••" />
        <LGoFormField
          name="confirmPassword"
          label="Potvrzení hesla"
          type="password"
          placeholder="••••••••"
        />
        <LGoFormField
          name="accessCode"
          label="Přístupový kód"
          placeholder="Zadejte přístupový kód"
          hint="Přístupový kód obdržíte od týmu LastGO"
        />
        <Controller
          name="acceptedTerms"
          control={form.control}
          render={({ field, fieldState }) => (
            <div data-field="acceptedTerms" className="flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="acceptedTerms"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  onBlur={field.onBlur}
                  aria-invalid={fieldState.invalid || undefined}
                  className="mt-0.5"
                />
                <label htmlFor="acceptedTerms" className="text-sm text-foreground">
                  Souhlasím s{" "}
                  <a
                    href={PARTNER_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-primary transition-colors"
                  >
                    obchodními podmínkami pro partnery
                  </a>
                </label>
              </div>
              {fieldState.error && (
                <p className="text-xs text-destructive">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />
        <LGoButton type="submit" variant="primary" loading={isLoading} className="w-full">
          Zaregistrovat se
        </LGoButton>
      </form>
    </FormProvider>
  );
}
